import React, { ChangeEventHandler, MouseEventHandler, useState } from 'react'
import styled from '@emotion/styled'
import type { CookieAttributes } from 'js-cookie'
import { Button } from '@mobi/component-library/Base/Buttons'
import { dayjs } from '@mobi/utils/date'
import * as CookieService from '@core/Utils/cookies'
import { ExpandableToolboxSection } from './Common/ExpandableToolboxSection'

const presetCookies = ['webapp-version', 'androidapp', 'iosapp']

const expiryOptions = [
  { label: 'Session', type: 'session', minutes: 0 },
  { label: 'Custom', type: 'custom', minutes: null },
  { label: '1 minute', type: 'minutes', minutes: 1 },
  { label: '1 hour', type: 'hours', minutes: 60 },
  { label: '1 day', type: 'days', minutes: 1_440 },
  { label: '1 week', type: 'weeks', minutes: 10_080 },
  { label: '1 month', type: 'months', minutes: 40_320 },
]

export const CookieManagement = () => {
  const [presetName, setPresetName] = useState('Custom')
  const [customName, setCustomName] = useState('')
  const [cookieValue, setCookieValue] = useState('')
  const [expiryType, setExpiryType] = useState(expiryOptions[0].type)
  const [expiryMinutes, setExpiryMinutes] = useState<number>()
  const [customExpiry, setCustomExpiry] = useState('')
  const [actionMessage, setActionMessage] = React.useState('')

  const getCookieName = () => presetName || customName?.trim()

  const setCookie: MouseEventHandler<HTMLButtonElement> = event => {
    event.preventDefault()

    const name = getCookieName()
    if (!name) {
      setActionMessage('Need a cookie name')
      return
    }

    let options: CookieAttributes | undefined = undefined

    switch (expiryType) {
      case 'custom': {
        options = {
          expires: new Date(customExpiry),
        }
        break
      }
      case 'minutes':
      case 'hours':
      case 'days':
      case 'weeks':
      case 'months':
        options = {
          expires: dayjs()
            .add(expiryMinutes ?? 0, 'm')
            .toDate(),
        }
    }

    CookieService.setCookieValue(name, cookieValue, options)

    setActionMessage(
      `Cookie "${name}" set to "${cookieValue}" with expiry "${options?.expires?.toString() ?? 'Session'}"`
    )
  }

  const clearCookie: MouseEventHandler<HTMLButtonElement> = event => {
    event.preventDefault()

    const name = getCookieName()
    if (!name || name === 'Custom') {
      setActionMessage('Need a cookie name')
      return
    }

    CookieService.clearCookieValue(name)

    setActionMessage(`Cleared cookie: ${name} (if exists)`)
  }

  const handleExpiryChange: ChangeEventHandler<HTMLSelectElement> = e => {
    const type = e.target.value
    setExpiryType(type)

    const opt = expiryOptions.find(o => o.type === type)
    if (opt?.minutes) setExpiryMinutes(opt.minutes)
  }

  return (
    <ExpandableToolboxSection heading='Manage Cookies'>
      <FormStyled>
        <label>Presets </label>
        <select value={presetName} onChange={e => setPresetName(e.target.value)}>
          <option value=''>Custom</option>
          {presetCookies.map(name => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </select>
        {presetName === '' && (
          <>
            <label>Cookie Name:</label>
            <input
              type='text'
              placeholder='Enter cookie name'
              value={customName}
              onChange={e => setCustomName(e.target.value)}
            />
          </>
        )}
        <label>Value:</label>
        <input
          type='text'
          placeholder='Enter cookie value'
          value={cookieValue}
          onChange={e => setCookieValue(e.target.value)}
        />
        <label>Expiry:</label>
        <select value={expiryType} onChange={handleExpiryChange}>
          {expiryOptions.map(opt => (
            <option key={opt.label} value={opt.type}>
              {opt.label}
            </option>
          ))}
        </select>
        {expiryType === 'custom' && (
          <>
            <label>Custom expiry date/time:</label>
            <input
              type='datetime-local'
              value={customExpiry}
              onChange={e => setCustomExpiry(e.target.value)}
            />
          </>
        )}
        <div />
        <div>
          <Button size='xs' onClick={setCookie}>
            Set Cookie
          </Button>
          &nbsp;
          <Button size='xs' onClick={clearCookie}>
            Clear Cookie
          </Button>
        </div>
      </FormStyled>

      {actionMessage && <p>{actionMessage}</p>}
    </ExpandableToolboxSection>
  )
}

const FormStyled = styled.form({
  display: 'grid',
  gridTemplateColumns: '1fr 2fr',
  gap: '1rem',

  '> label': {
    fontSize: '1.4rem',
  },
})
